import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import SecureLS from 'secure-ls';
import { setUser, setFamilies as setSecureFamilies } from '../../utils/secureStorage';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const ls = new SecureLS({ encodingType: 'aes' });

// Step indicator dot
function StepDot({ n, active, done }) {
  const bg = done ? '#556B2F' : active ? '#E65100' : '#F2F2F2';
  const border = done ? '#556B2F' : active ? '#E65100' : '#CCCCCC';
  const color = done || active ? '#fff' : '#666666';
  return (
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
      style={{ background: bg, border: `2px solid ${border}`, color }}
      aria-label={`Step ${n}${done ? ' completed' : active ? ' current' : ''}`}
    >
      {done ? '✓' : n}
    </div>
  );
}

function StepLine({ done }) {
  return <div className="flex-1 h-0.5 mx-1" style={{ background: done ? '#556B2F' : '#CCCCCC' }} />;
}

// Password strength meter
function PasswordStrengthMeter({ password }) {
  if (!password.length) return null;
  const pct = Math.min(100, (password.length / 12) * 100);
  const color = password.length < 6 ? '#dc2626' : password.length < 8 ? '#E65100' : '#556B2F';
  const label = password.length < 6 ? 'Too short' : password.length < 8 ? 'Almost there' : 'Strong ✓';
  return (
    <div className="mt-1 mb-3">
      <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-300" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="text-xs mt-1" style={{ color }}>{label}</div>
    </div>
  );
}

function AcceptInvitation() {
  const navigate = useNavigate();
  const { id, token } = useParams();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [invitationData, setInvitationData] = useState(null);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const passwordValid = formData.password.length >= 8 && formData.password === formData.confirmPassword;
  const canSubmit = passwordValid && termsAccepted;

  useEffect(() => {
    validateInvitation();
  }, [id, token]);

  const validateInvitation = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/invitations/${id}/details/${token}/`
      );

      if (response.data.success) {
        const invitation = response.data.invitation;
        const invitationInfo = {
          family_name: invitation.family,
          invited_by: typeof invitation.invited_by === 'object'
            ? `${invitation.invited_by.first_name} ${invitation.invited_by.last_name || ''}`.trim()
            : invitation.invited_by,
          relationship_type: invitation.relationship_type_reverse || invitation.relationship_type,
          invited_first_name: invitation.invited_first_name,
          invited_last_name: invitation.invited_last_name,
          invited_email: invitation.invited_email,
          member_count: invitation.member_count || 0,
        };
        setInvitationData(invitationInfo);
        setFormData(prev => ({
          ...prev,
          first_name: invitation.invited_first_name || '',
          last_name: invitation.invited_last_name || '',
          email: invitation.invited_email || ''
        }));
      } else {
        setError(response.data.error || 'Invalid invitation');
      }
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Unable to validate invitation. The link may be invalid or expired.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const response = await axios.post(
        `${API_BASE_URL}/api/invitations/${id}/accept/${token}/`,
        {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          password: formData.password,
          confirm_password: formData.confirmPassword
        }
      );

      if (response.data.success) {
        if (response.data.token) {
          ls.set('authToken', response.data.token);
          localStorage.setItem('isLoggedIn', 'true');
        }
        if (response.data.person) {
          setUser(response.data.person);
        }
        if (response.data.family_id) {
          localStorage.setItem('familyId', response.data.family_id);
          // Store families array so FamilySwitcher shows immediately
          const familyData = [{
            id: response.data.family_id,
            name: response.data.family_name || invitationData?.family_name || 'Family',
            role: 'member',
          }];
          setSecureFamilies(familyData);
        }
        setStep(3);
      } else {
        setError(response.data.error || 'Failed to accept invitation');
      }
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inviteeName = invitationData
    ? `${invitationData.invited_first_name || ''} ${invitationData.invited_last_name || ''}`.trim()
    : '';

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-[#eae9d9] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#556B2F] mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  // Error (no invitation data)
  if (error && !invitationData) {
    return (
      <div className="min-h-screen bg-[#eae9d9] flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl border border-[#CCCCCC] p-8 text-center">
            <div className="text-5xl mb-4">⚠️</div>
            <h1 className="text-xl font-bold text-[#333333] mb-3">Invalid Invitation</h1>
            <p className="text-sm text-[#666666] mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-[#556B2F] text-white px-6 py-3 rounded-lg font-bold text-sm hover:bg-[#4A5D23] transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#eae9d9] flex items-center justify-center py-8 px-4">
      <div className="w-full max-w-[440px]">
        {/* Step Indicator */}
        <div className="flex items-center mb-6" aria-label={`Step ${step} of 3`}>
          <StepDot n={1} active={step === 1} done={step > 1} />
          <StepLine done={step > 1} />
          <StepDot n={2} active={step === 2} done={step > 2} />
          <StepLine done={step > 2} />
          <StepDot n={3} active={step === 3} done={false} />
        </div>

        {/* Step 1 — Landing / Review */}
        {step === 1 && (
          <div className="bg-white rounded-xl border border-[#CCCCCC] p-6">
            <div className="text-center mb-5">
              <div className="text-[11px] font-bold text-[#556B2F] tracking-wider uppercase mb-3">One Generation to Next</div>
              <div className="text-[32px] mb-2">👋</div>
              <div className="text-xl font-bold text-[#333333]">You've been invited!</div>
              <div className="text-[13px] text-[#666666] mt-2 leading-relaxed">
                {invitationData.invited_by} has invited you to join<br />
                <strong>{invitationData.family_name}</strong>
              </div>
            </div>

            {/* Inviter card */}
            <div className="flex items-center gap-3 bg-[#F5F5DC] rounded-[10px] p-3 mb-3.5">
              <div className="w-11 h-11 rounded-full bg-[#556B2F] flex items-center justify-center text-white font-bold flex-shrink-0">
                {invitationData.invited_by?.[0]?.toUpperCase() || '?'}
              </div>
              <div>
                <div className="text-sm font-bold text-[#333333]">{invitationData.invited_by}</div>
                <div className="text-xs text-[#666666]">
                  invited you as {invitationData.relationship_type ? `their ${invitationData.relationship_type}` : 'a family member'}
                </div>
              </div>
            </div>

            {/* Family badge */}
            <div className="flex items-center gap-3 bg-[rgba(85,107,47,0.07)] border border-[rgba(85,107,47,0.2)] rounded-[10px] p-3 mb-3.5">
              <div className="w-10 h-10 bg-[#556B2F] rounded-lg flex items-center justify-center text-xl flex-shrink-0">🏡</div>
              <div>
                <div className="text-[13px] font-bold text-[#333333]">
                  {invitationData.family_name}
                  {invitationData.member_count > 0 && ` · ${invitationData.member_count} members`}
                </div>
                <div className="text-[11px] text-[#666666] mt-0.5">
                  invited by {invitationData.invited_by}
                  {invitationData.relationship_type && ` as ${invitationData.relationship_type}`}
                </div>
              </div>
            </div>

            {/* Pre-filled details */}
            <div className="text-xs font-semibold text-[#333333] mb-1.5">Your details</div>
            <div className="bg-[#F2F2F2] rounded-lg p-3 mb-5">
              <div className="text-[13px] font-semibold text-[#333333]">{inviteeName}</div>
              {invitationData.invited_email && (
                <div className="text-xs text-[#666666] mt-0.5">{invitationData.invited_email}</div>
              )}
              <div className="text-[11px] text-[#999999] mt-1">
                ✎ Not you? Ask {invitationData.invited_by?.split(' ')[0]} to resend with the correct details.
              </div>
            </div>

            <button
              onClick={() => setStep(2)}
              className="w-full bg-[#556B2F] text-white py-3 rounded-lg font-bold text-[13px] hover:bg-[#4A5D23] transition-colors"
            >
              Accept Invitation →
            </button>
            <div className="text-center text-[11px] text-[#999999] mt-2.5">
              By accepting you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline text-[#556B2F] hover:text-[#6B8E23]">Terms</a>
              {' & '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline text-[#556B2F] hover:text-[#6B8E23]">Privacy Policy</a>
            </div>
          </div>
        )}

        {/* Step 2 — Set Password */}
        {step === 2 && (
          <div className="bg-white rounded-xl border border-[#CCCCCC] p-6">
            <div className="text-lg font-bold text-[#333333] mb-1">Create your password</div>
            <div className="text-[13px] text-[#666666] mb-5">
              You're joining as <strong>{inviteeName}</strong>. Set a password to secure your account.
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-4 text-xs">
                {error}
              </div>
            )}

            {/* Password */}
            <div className="mb-4">
              <div className="text-[13px] font-semibold text-[#333333] mb-1.5">
                Password <span className="text-[#E65100]">*</span>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="At least 8 characters"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-3.5 py-3 text-sm border border-[#CCCCCC] rounded-lg focus:outline-none focus:border-[#556B2F]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999999] hover:text-[#666666] text-xs"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <PasswordStrengthMeter password={formData.password} />
            </div>

            {/* Confirm password */}
            <div className="mb-4">
              <div className="text-[13px] font-semibold text-[#333333] mb-1.5">
                Confirm password <span className="text-[#E65100]">*</span>
              </div>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Repeat your password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="w-full px-3.5 py-3 text-sm border border-[#CCCCCC] rounded-lg focus:outline-none focus:border-[#556B2F]"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999999] hover:text-[#666666] text-xs"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {formData.confirmPassword.length > 0 && formData.password !== formData.confirmPassword && (
                <div className="text-xs text-[#dc2626] mt-1">⚠️ Passwords don't match</div>
              )}
            </div>

            {/* Terms of Service */}
            <div className="mb-4 mt-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-0.5 w-5 h-5 rounded border-2 border-[#CCCCCC] text-[#556B2F] focus:ring-[#556B2F] cursor-pointer"
                  style={{ accentColor: '#556B2F' }}
                />
                <span className="text-[13px] text-[#333333] leading-relaxed">
                  I agree to the{' '}
                  <a
                    href="https://1g2n.com/terms.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold underline"
                    style={{ color: '#556B2F' }}
                  >
                    Terms of Service
                  </a>
                </span>
              </label>
            </div>

            <div className="flex gap-2.5">
              <button
                onClick={() => { setStep(1); setError(null); }}
                className="px-4 py-2.5 text-[13px] font-bold text-[#666666] border border-[#666666] rounded-lg bg-transparent hover:bg-gray-50 transition-colors"
              >
                ← Back
              </button>
              <button
                onClick={() => canSubmit && handleSubmit()}
                disabled={!canSubmit || submitting}
                className="flex-1 py-2.5 text-[13px] font-bold text-white rounded-lg transition-colors disabled:cursor-not-allowed"
                style={{ background: canSubmit && !submitting ? '#556B2F' : '#bbb' }}
              >
                {submitting ? 'Creating account...' : 'Join Family'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Welcome */}
        {step === 3 && (
          <div className="bg-white rounded-xl border border-[#CCCCCC] p-6 text-center">
            <div className="text-[52px] mb-3">🎉</div>
            <div className="text-[22px] font-bold text-[#333333] mb-2">Welcome to the family!</div>
            <div className="text-[13px] text-[#666666] mb-5 leading-relaxed">
              You're now part of <strong>{invitationData.family_name}</strong>.<br />
              {invitationData.invited_by?.split(' ')[0]} can see you've joined.
            </div>

            <div className="bg-[#F5F5DC] rounded-[10px] p-4 mb-5 text-left">
              <div className="text-xs font-bold text-[#556B2F] mb-2.5">You now have access to:</div>
              {['Family stories and memories', 'Family tree', 'Time capsules addressed to you', 'Daily storytelling prompts'].map(item => (
                <div key={item} className="text-xs text-[#333333] py-1 flex gap-2">
                  <span className="text-[#556B2F]">✓</span>{item}
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate('/family-hub')}
              className="w-full bg-[#556B2F] text-white py-3 rounded-lg font-bold text-[13px] hover:bg-[#4A5D23] transition-colors"
            >
              Go to My Family →
            </button>
            <div className="text-[11px] text-[#666666] mt-2.5">
              You can update your profile anytime in Settings.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AcceptInvitation;
